/**
 * Truck occupancy maths - the frontend half of the server's
 * DeliveryOccupancyService.
 *
 * A cube = one loading position. An item's span is
 * `order_line.size * order_line.qty` - never persisted, re-derived on every
 * read, because a split changes the line underneath an allocation.
 *
 * Positions 1-7 are the truck bed, 8-13 the trailer. A run with
 * `number_of_positions` of 7 is truck-only and has no trailer.
 *
 * This module exists because the occupancy maths used to be written out
 * four times, and each copy was slightly different:
 *   - TruckGraphic's `checkCapacityForItem` and `findFirstAvailablePosition`
 *     looked only at `items[0]` of each delivery, so a delivery holding
 *     several items (a split order's pieces) was invisible to them
 *   - the reorder-drop validated `items[0]` rather than the item dragged
 *   - PlanningPage had two separate counters that gave two answers
 *
 * The server enforces the same rules; this module is what the planner uses
 * to tell the operator where there IS room, before they try.
 */

/** Positions 1-7 are the truck bed. */
export const TRUCK_POSITIONS = 7;

/**
 * The number of loading positions an item occupies.
 */
export const itemSpan = (item) => {
  const line = item?.order_line;
  if (!line) return 1;
  return Math.max(1, (line.size || 1) * (line.qty || 1));
};

/**
 * Every position occupied across a set of deliveries, as a Set.
 *
 * Counts EVERY item of EVERY delivery - not just the first item of each.
 *
 * @param {Array} deliveries
 * @param {number|null} excludeItemId - a delivery_item being moved, so it
 *   does not block its own new position
 * @returns {Set<number>}
 */
export const occupiedPositions = (deliveries, excludeItemId = null) => {
  const occupied = new Set();

  (deliveries || []).forEach((delivery) => {
    (delivery?.items || []).forEach((item) => {
      if (excludeItemId !== null && Number(item.id) === Number(excludeItemId)) {
        return;
      }

      const start = Number(item.loading_position);
      if (!start) return;

      const span = itemSpan(item);
      for (let i = 0; i < span; i++) {
        occupied.add(start + i);
      }
    });
  });

  return occupied;
};

/**
 * The positions on a run that are occupied, within the run's range.
 *
 * A position outside the run's range (legacy data from before the server
 * guard existed) is not counted - it is not really filling the truck, and
 * counting it made the sidebar say fewer positions were free than the
 * API said were filled.
 *
 * @returns {Set<number>}
 */
export const occupiedPositionsInRange = (deliveries, numberOfPositions) => {
  const occupied = occupiedPositions(deliveries);
  const inRange = new Set();

  occupied.forEach((position) => {
    if (position >= 1 && position <= numberOfPositions) inRange.add(position);
  });

  return inRange;
};

/**
 * How many of a run's positions are occupied.
 *
 * A position outside the run's range (legacy data from before the server
 * guard existed) is not counted - it is not really filling the truck.
 */
export const filledPositions = (deliveries, numberOfPositions) =>
  occupiedPositionsInRange(deliveries, numberOfPositions).size;

/**
 * The positions on a run that are still free.
 */
export const freePositions = (deliveries, numberOfPositions) => {
  const occupied = occupiedPositionsInRange(deliveries, numberOfPositions);
  const free = [];

  for (let position = 1; position <= numberOfPositions; position++) {
    if (!occupied.has(position)) free.push(position);
  }

  return free;
};

/**
 * Check a proposed placement against a run.
 *
 * Rejects:
 *  - a position beyond the run's `number_of_positions`
 *  - a span that runs off the end of the truck or trailer
 *  - a span that straddles the 7/8 truck-trailer boundary
 *  - a span that overlaps a position already occupied
 *
 * @returns {{valid: boolean, message: string, code: string|null}}
 */
export const checkPlacement = (
  deliveries,
  start,
  span,
  numberOfPositions,
  excludeItemId = null,
) => {
  const positions = [];
  for (let i = 0; i < Math.max(1, span); i++) positions.push(start + i);

  if (start < 1) {
    return {
      valid: false,
      message: 'Loading position must be 1 or greater.',
      code: 'position_out_of_range',
    };
  }

  const beyond = positions.find((position) => position > numberOfPositions);
  if (beyond !== undefined) {
    return {
      valid: false,
      message:
        `Position ${beyond} is beyond this run's ${numberOfPositions} positions. ` +
        'A truck and trailer has 13 positions (7 truck, 6 trailer); a truck-only run has 7.',
      code: 'position_out_of_range',
    };
  }

  // The truck and the trailer are separate vehicles. An item cannot sit
  // half on one and half on the other.
  if (numberOfPositions > TRUCK_POSITIONS) {
    const last = Math.max(...positions);
    if (start <= TRUCK_POSITIONS && last > TRUCK_POSITIONS) {
      return {
        valid: false,
        message:
          `A ${span}-cube item starting at position ${start} would straddle the ` +
          `truck and the trailer. Split it, or start it at position ` +
          `${TRUCK_POSITIONS + 1} or later.`,
        code: 'straddles_truck_and_trailer',
      };
    }
  }

  const occupied = occupiedPositions(deliveries, excludeItemId);
  const clash = positions.find((position) => occupied.has(position));
  if (clash !== undefined) {
    return {
      valid: false,
      message: `Position ${clash} is already occupied by another item on this run.`,
      code: 'position_occupied',
    };
  }

  return { valid: true, message: '', code: null };
};

/**
 * The highest loading position a delivery occupies.
 *
 * The run sheet sorts the stops by this, and the API assigns the note
 * keys (N1, N2, ...) in the same order - so a stop's note key matches
 * the order the driver reads the sheet in.
 *
 * A delivery with no items returns 0, which sorts last among the real
 * positions. (The old inline version used `Math.max(...[])` and got
 * -Infinity for an empty delivery, which is a different answer to the
 * same question in two places.)
 */
export const maxLoadingPosition = (delivery) => {
  const items = delivery?.items || [];
  let max = 0;

  items.forEach((item) => {
    const position = Number(item.loading_position) || 0;
    if (position > max) max = position;
  });

  return max;
};

/**
 * Sort deliveries the way the run sheet prints them: by loading
 * position, highest first.
 *
 * The sort is stable, so deliveries sharing a position keep the order
 * the API sent them in.
 */
export const sortByLoadingPosition = (deliveries) =>
  [...(deliveries || [])].sort(
    (a, b) => maxLoadingPosition(b) - maxLoadingPosition(a),
  );

/**
 * Find the first position where an item will fit.
 *
 * Tries the truck first (1-7), then the trailer (8-13), so the load is
 * packed from the front.
 *
 * @returns {{position: number|null, section: 'truck'|'trailer'|null}}
 */
export const firstAvailablePosition = (item, deliveries, numberOfPositions) => {
  const span = itemSpan(item);
  const occupied = occupiedPositions(deliveries);

  const fits = (start, end) => {
    for (let position = start; position <= end; position++) {
      if (occupied.has(position)) return false;
    }
    return true;
  };

  for (let start = 1; start <= TRUCK_POSITIONS; start++) {
    const end = start + span - 1;
    if (end > TRUCK_POSITIONS) continue;
    if (fits(start, end)) return { position: start, section: 'truck' };
  }

  if (numberOfPositions > TRUCK_POSITIONS) {
    for (let start = TRUCK_POSITIONS + 1; start <= numberOfPositions; start++) {
      const end = start + span - 1;
      if (end > numberOfPositions) continue;
      if (fits(start, end)) return { position: start, section: 'trailer' };
    }
  }

  return { position: null, section: null };
};
